import type { VoiceShape } from "@/lib/audio/voices";
import workletUrl from "@/lib/audio/voiceWorklet.js?url";

const PROCESSOR = "bsmeet-voice";

const modules = new WeakMap<BaseAudioContext, Promise<void>>();

function loadModule(context: BaseAudioContext): Promise<void> {
  const already = modules.get(context);
  if (already !== undefined) return already;
  const pending = context.audioWorklet.addModule(workletUrl);
  modules.set(context, pending);
  pending.catch(() => {
    modules.delete(context);
  });
  return pending;
}

export function voiceSupported(): boolean {
  return (
    typeof globalThis.AudioWorkletNode === "function" &&
    typeof globalThis.AudioContext === "function"
  );
}

export class VoiceChanger {
  #context: AudioContext;
  #input: GainNode;
  #output: GainNode;
  #node: AudioWorkletNode | null = null;
  #shape: VoiceShape = { pitch: 1, formant: 1, monotone: 0 };
  #wanted = false;
  #spliced = false;
  #building = false;
  #stopped = false;

  constructor(context: AudioContext) {
    this.#context = context;
    this.#input = context.createGain();
    this.#output = context.createGain();
    this.#input.connect(this.#output);
  }

  get input(): AudioNode {
    return this.#input;
  }

  get output(): AudioNode {
    return this.#output;
  }

  apply(shape: VoiceShape): void {
    if (this.#stopped) return;
    this.#shape = shape;
    this.#wanted = shape.monotone > 0 || shape.pitch !== 1 || shape.formant !== 1;
    this.#node?.port.postMessage(shape);
    if (!this.#wanted) {
      this.#bypass();
      return;
    }
    if (this.#node === null) this.#build();
    else this.#splice();
  }

  stop(): void {
    this.#stopped = true;
    this.#wanted = false;
    this.#spliced = false;
    this.#node?.disconnect();
    this.#node = null;
    this.#input.disconnect();
    this.#output.disconnect();
  }

  #build(): void {
    if (this.#building || !voiceSupported()) return;
    this.#building = true;
    loadModule(this.#context)
      .then(() => {
        this.#building = false;
        if (this.#stopped || this.#node !== null) return;
        const node = new AudioWorkletNode(this.#context, PROCESSOR, {
          numberOfInputs: 1,
          numberOfOutputs: 1,
          outputChannelCount: [1],
          channelCount: 1,
          channelCountMode: "explicit",
        });
        this.#node = node;
        node.port.postMessage(this.#shape);
        if (this.#wanted) this.#splice();
      })
      .catch(() => {
        this.#building = false;
      });
  }

  #splice(): void {
    const node = this.#node;
    if (node === null || this.#spliced || this.#stopped) return;
    this.#input.disconnect(this.#output);
    this.#input.connect(node);
    node.connect(this.#output);
    this.#spliced = true;
  }

  #bypass(): void {
    const node = this.#node;
    if (!this.#spliced || node === null) return;
    this.#input.disconnect(node);
    node.disconnect(this.#output);
    this.#input.connect(this.#output);
    this.#spliced = false;
  }
}
